import { useCallback, useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import * as ApiModels from '@/typings/questionnaires-api-models';
import { getNoodleDb } from '../rxdb.noodle';
import { getQuestionnaireResponseReplicationIdentifier, startOrResyncReplication } from '../rxdb.replication';

type Config = {
  realtime?: boolean;
  enabled?: boolean;
  refetchOnMount?: boolean;
};

type QuestionnaireResponses = ApiModels.QuestionnaireResponse & {
  listResponses: Array<ApiModels.QuestionnaireResponse> | null;
};

function useQuestionnaireResponses(
  questionnaireId?: string,
  { realtime = true, enabled = true, refetchOnMount = true }: Config = {},
): QuestionnaireResponses[] | null {
  const subscription = useRef<Subscription | null>(null);
  const hasBeenRefetchedOnMount = useRef(false);

  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponses[] | null>(null);

  const getQuestionnaireResponses = useCallback(async () => {
    try {
      if (!questionnaireId) return;

      const db = await getNoodleDb();

      subscription.current = db.questionnaireResponse
        .find({ selector: { questionnaireId }, sort: [{ createdAt: 'desc' }] })
        .$.subscribe(rxQuestionnaireResponses => {
          const responses = rxQuestionnaireResponses.map(rxQuestionnaireResponse => {
            const questionnaireResponse = rxQuestionnaireResponse.toMutableJSON() as unknown as QuestionnaireResponses;
            return questionnaireResponse;
          }).sort((a, b) => a.index - b.index) as QuestionnaireResponses[];

          setQuestionnaireResponses(responses);

          // If not realtime, unsubscribe after first value is set
          if (!realtime) {
            subscription.current?.unsubscribe();
          }
        });
    } catch (error) {
      console.error('RXDB useQuestionnaireResponses error - getQuestionnaireResponses()', error);
    }
  }, [questionnaireId, realtime]);

  const handleRefetch = useCallback(() => {
    if (!questionnaireId) return;

    const identifier = getQuestionnaireResponseReplicationIdentifier(questionnaireId);
    startOrResyncReplication(identifier);
  }, [questionnaireId]);

  const handleRefetchOnMount = useCallback(() => {
    if (!refetchOnMount || hasBeenRefetchedOnMount.current) return;

    hasBeenRefetchedOnMount.current = true;
    handleRefetch();
  }, [handleRefetch, refetchOnMount]);

  useEffect(() => {
    if (enabled) {
      getQuestionnaireResponses();
      handleRefetchOnMount();
    }

    return () => {
      subscription.current?.unsubscribe();
    };
  }, [getQuestionnaireResponses, enabled, handleRefetchOnMount]);

  return questionnaireResponses;
}

export default useQuestionnaireResponses;
